import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PublicUserEntity } from './public-user.entity';
import { tinyintBoolean } from './tinyint-boolean.transformer';

/**
 * PD-16475 — an in-app (bell) notification for one storefront user.
 *
 * The portal counterpart of `user_notification`. It is a separate table
 * because `public_user` and the CRM `_user` table share one integer id space:
 * a row keyed on "user 12" could not say whether it belongs to a customer or
 * an employee. Columns mirror `user_notification` so the same bell tray
 * (libs/web-ui NotificationsPanel) renders both.
 */
@Index('public_user_notification_user_unread_idx', ['fkPublicUserId', 'isRead'])
@Index('public_user_notification_user_created_idx', [
	'fkPublicUserId',
	'createdDate',
])
@Index('public_user_notification_user_dismissed_idx', [
	'fkPublicUserId',
	'dismissedAt',
])
@Index('public_user_notification_dedupe_idx', ['fkPublicUserId', 'dedupeKey'])
@Entity('public_user_notification')
export class PublicUserNotificationEntity extends BaseEntity {
	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	/** The event, a PORTAL_NOTIFICATION_TYPE value (free-form at the DB layer). */
	@Column('varchar', { name: 'notificationType', length: 64 })
	public notificationType!: string;

	/** Broad bucket, a PORTAL_NOTIFICATION_CATEGORY value; drives the tray icon. */
	@Column('varchar', { name: 'category', length: 32 })
	public category!: string;

	@Column('varchar', { name: 'title', length: 255 })
	public title!: string;

	@Column('text', { name: 'body', nullable: true })
	public body!: string | null;

	/**
	 * What the notification is about, e.g. `'issue' / 42`. The storefront maps
	 * the pair to a route (`/support/42`); null for a notice about nothing.
	 */
	@Column('varchar', { name: 'entityType', length: 64, nullable: true })
	public entityType!: string | null;

	@Column('int', { name: 'entityId', unsigned: true, nullable: true })
	public entityId!: number | null;

	@Column('tinyint', {
		name: 'isRead',
		width: 1,
		default: () => "'0'",
		transformer: tinyintBoolean,
	})
	public isRead!: boolean;

	@Column('datetime', { name: 'readAt', nullable: true })
	public readAt!: Date | null;

	/** When the user removed it from the tray (the per-row ×); null = still listed. */
	@Column('datetime', { name: 'dismissedAt', nullable: true })
	public dismissedAt!: Date | null;

	/** Optional TTL; an expired row is left out of the tray. */
	@Column('datetime', { name: 'expiresAt', nullable: true })
	public expiresAt!: Date | null;

	/**
	 * Idempotency key, scoped per user. A second write with the same key
	 * refreshes the existing row (keeping its read state) instead of stacking a
	 * duplicate, e.g. a reply notification fired twice for the same note.
	 */
	@Column('varchar', { name: 'dedupeKey', length: 128, nullable: true })
	public dedupeKey!: string | null;

	@ManyToOne(() => PublicUserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_public_user__id', referencedColumnName: 'id' }])
	public fkPublicUser!: PublicUserEntity;
}
